/**
 * Onboarding API endpoints and types
 *
 * Provides access to the onboarding workflow endpoints for firms
 * to submit answers to global questions.
 */

import apiClient from './client';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/**
 * A single onboarding question for a firm, with its current answer state.
 * `response_text` is null when no answer has been recorded. Used as the
 * element type of `OnboardingQuestionsResponse.questions`.
 */
export interface OnboardingQuestion {
  id: number;
  vector_id: string;
  question_text: string;
  response_text: string | null;
  source_type: string;
  category: string | null;
  tags: string[];
  firm_id: number | null;
  usage_count: number;
  created_at: string;
  is_stale: boolean;
}

/**
 * Paginated set of onboarding questions returned by `getOnboardingQuestions`.
 * `total` is the full count matching the filter; `offset` and `limit` describe
 * the returned page.
 */
export interface OnboardingQuestionsResponse {
  questions: OnboardingQuestion[];
  total: number;
  offset: number;
  limit: number;
}

/**
 * Request body for `submitOnboardingAnswer`: the firm, the canonical question
 * being answered, and the answer text.
 */
export interface SubmitAnswerRequest {
  firm_id: number;
  canonical_question_id: number;
  answer_text: string;
}

/**
 * Result of `submitOnboardingAnswer`. `status` is `'created'` for a new entry
 * or `'duplicate'` when a matching answer already exists; `entry_id` identifies
 * the resulting answer entry.
 */
export interface SubmitAnswerResponse {
  status: 'created' | 'duplicate';
  entry_id: number;
  message?: string;
}

/**
 * Onboarding completion stats for a firm, as returned by
 * `getOnboardingProgress`. `percent` is the completion percentage; `by_category`
 * breaks the total and answered counts down per question category.
 */
export interface OnboardingProgress {
  total: number;
  answered: number;
  percent: number;
  by_category: Record<string, { total: number; answered: number }>;
}

/**
 * Query filters accepted by `getOnboardingQuestions`. `firm_id` is required;
 * `category`, `offset`, and `limit` are optional and narrow or page the result.
 */
export interface OnboardingQuestionFilters {
  firm_id: number;
  category?: string | null;
  offset?: number;
  limit?: number;
}

// -----------------------------------------------------------------------------
// API Endpoints
// -----------------------------------------------------------------------------

const ONBOARDING_BASE = '/v1/insight/onboarding';

/**
 * Get unanswered onboarding questions for a firm
 */
export async function getOnboardingQuestions(
  filters: OnboardingQuestionFilters
): Promise<OnboardingQuestionsResponse> {
  const params: Record<string, string | number> = {
    firm_id: filters.firm_id,
  };

  if (filters.category) params.category = filters.category;
  if (filters.offset !== undefined) params.offset = filters.offset;
  if (filters.limit !== undefined) params.limit = filters.limit;

  const response = await apiClient.get<OnboardingQuestionsResponse>(
    `${ONBOARDING_BASE}/questions`,
    { params }
  );
  return response.data;
}

/**
 * Submit an onboarding answer for a firm
 */
export async function submitOnboardingAnswer(
  request: SubmitAnswerRequest
): Promise<SubmitAnswerResponse> {
  const response = await apiClient.post<SubmitAnswerResponse>(
    `${ONBOARDING_BASE}/answers`,
    request
  );
  return response.data;
}

/**
 * Get onboarding progress for a firm
 */
export async function getOnboardingProgress(
  firmId: number
): Promise<OnboardingProgress> {
  const response = await apiClient.get<OnboardingProgress>(
    `${ONBOARDING_BASE}/progress`,
    { params: { firm_id: firmId } }
  );
  return response.data;
}
